import { Component, Input, Output, EventEmitter, inject, computed, model, Renderer2, effect, OnDestroy, OnChanges } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { LocalizationService } from '@abp/ng.core';
import { Confirmation, ModalComponent } from '@abp/ng.theme.shared';
import { localizeWithFallback } from '../utils/localization-utils';

import { FieldDefinition } from '../fields/field-definition';
import { PreAccessFilterValue } from './models/pre-access-filter.models';
import { PreAccessFilterField } from './pre-access-filter-field/pre-access-filter-field';
import { ZnButton } from '../zn-button/zn-button';
import { ZnConfirmation } from '../services/zn-confirmation';

@Component({
  selector: 'app-pre-access-filter-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModalComponent,
    PreAccessFilterField,
    ZnButton,
  ],
  templateUrl: './pre-access-filter-modal.html',
  styleUrls: ['./pre-access-filter-modal.scss'],
})
export class PreAccessFilterModal implements OnDestroy, OnChanges {
  private readonly localizationService = inject(LocalizationService);
  private readonly confirmationService = inject(ZnConfirmation);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly body = this.document.body;

  private suppressNextClosedEmit = false;
  private preserveFiltersOnNextOpen = false;

  constructor() {
    effect(() => {
      if (this.isVisible()) {
        this.renderer.addClass(this.body, 'pre-access-filter-open');
      } else {
        this.renderer.removeClass(this.body, 'pre-access-filter-open');
      }
    });
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.body, 'pre-access-filter-open');
  }

  @Input() fields: FieldDefinition[] = [];
  @Input() tableName: string = '';
  @Input() appliedFilters: PreAccessFilterValue[] = [];

  /** Two-way binding for modal visibility */
  isVisible = model<boolean>(false);

  @Output() filtersApplied = new EventEmitter<PreAccessFilterValue[]>();
  @Output() filtersClosed = new EventEmitter<void>();

  filterForm = new FormGroup<Record<string, FormControl<PreAccessFilterValue | null>>>({});

  modalTitle = computed(() =>
    localizeWithFallback(this.localizationService, 'LanguageService::PreAccessFiltersTitle')
  );

  tableLabel = computed(() => {
    if (!this.tableName) return '';
    return localizeWithFallback(this.localizationService, this.tableName);
  });

  ngOnChanges(): void {
    this.buildForm();
  }

  private buildForm(): void {
    Object.keys(this.filterForm.controls).forEach(key => {
      this.filterForm.removeControl(key);
    });

    this.fields.forEach(field => {
      const key = field.key;
      const initialValue = this.buildInitialValue(field);
      this.filterForm.addControl(key, new FormControl<PreAccessFilterValue | null>(initialValue));
    });
  }

  private buildInitialValue(field: FieldDefinition): PreAccessFilterValue | null {
    const applied = this.appliedFilters?.find(f => f.columnId === field.key);
    if (applied) {
      return {
        ...applied,
        queryKey: applied.queryKey ?? field.fieldOptions?.prefilter?.queryKey,
      };
    }

    const prefilter = field.fieldOptions?.prefilter;
    if (!prefilter?.defaultValue) {
      return null;
    }

    return {
      columnId: field.key,
      queryKey: field.fieldOptions?.prefilter?.queryKey,
      searchTerms: Array.isArray(prefilter.defaultValue)
        ? prefilter.defaultValue
        : [prefilter.defaultValue],
      operator: 'EQ',
      mode: prefilter.defaultMode || 'equal',
    };
  }

  onVisibleChange(visible: boolean): void {
    if (visible) {
      this.isVisible.set(true);
      this.suppressNextClosedEmit = false;
      if (this.preserveFiltersOnNextOpen) {
        this.preserveFiltersOnNextOpen = false;
        return;
      }
      this.buildForm();
      return;
    }
    if (this.suppressNextClosedEmit) {
      this.suppressNextClosedEmit = false;
      this.isVisible.set(false);
      return;
    }
    if (this.isVisible()) {
      this.requestCloseConfirmation();
    }
  }

  onApplyFilters(): void {
    const filters: PreAccessFilterValue[] = [];

    Object.entries(this.filterForm.controls).forEach(([key, control]) => {
      const value = control.value;
      if (value && value.searchTerms && value.searchTerms.length > 0) {
        const validTerms = value.searchTerms.filter(
          (t: any) => t !== null && t !== undefined
        );
        if (validTerms.length > 0 || value.mode === 'range') {
          filters.push({
            ...value,
            searchTerms: value.searchTerms,
          });
        }
      }
    });

    this.suppressNextClosedEmit = true;
    this.filtersApplied.emit(filters);
    this.isVisible.set(false);
  }

  onClose(): void {
    this.requestCloseConfirmation();
  }

  private requestCloseConfirmation(): void {
    this.isVisible.set(false);
    this.showCloseConfirmation().subscribe((status) => {
      if (status === Confirmation.Status.confirm) {
        this.filtersClosed.emit();
        return;
      }

      this.preserveFiltersOnNextOpen = true;
      this.isVisible.set(true);
    });
  }

  private showCloseConfirmation() {
    return this.confirmationService.showCancelConfirmation({
      messageKey: 'LanguageService::ConfirmationExitPreAccessFilter',
      titleKey: 'LanguageService::ConfirmationExitPreAccessFilterTitle',
    });
  }

  translate(key: string): string {
    return localizeWithFallback(this.localizationService, key);
  }
}
